<script>
	import { onMount, tick } from 'svelte';
	import Hydrator from '@reperio-news/core.components/Hydrator.svelte';
	import { fetchSectionNews } from '@reperio-news/adh-www-common/src/shared/blocks/FinanceStrip/finance.js';
	import Config from '../../index.js';
	import { fetchMarket, usableMarket, sampleMarket, number, price, turnover, change, direction, quoteLabel, INDEX_NAMES } from './market.js';
	export let data = {};
	export let id;
	export let globalId = null;
	export let globalSource = null;
	id; globalId; globalSource;
	let market = null;
	let loaded = false;
	let news = [];
	let newsLoaded = false;
	let newsLoading = false;
	let quotes;
	let canBack = false;
	let canForward = false;
	let reducedMotion = true;
	function measureQuotes() {
		if (!quotes) return;
		canBack = quotes.scrollLeft > 2;
		canForward = quotes.scrollWidth - quotes.clientWidth - quotes.scrollLeft > 2;
	}
	function watchQuotes(node) {
		quotes = node;
		let destroyed = false;
		const observer = new ResizeObserver(measureQuotes);
		function observe() {
			if (destroyed) return;
			observer.disconnect();
			observer.observe(node);
			for (const child of node.children) observer.observe(child);
			measureQuotes();
		}
		observe();
		return { update: () => tick().then(observe), destroy: () => { destroyed = true; observer.disconnect(); } };
	}
	function browseQuotes(direction) {
		quotes?.scrollBy({ left: direction * Math.max(150, quotes.clientWidth * .75), behavior: reducedMotion ? 'instant' : 'smooth' });
	}
	$: if (__ADMIN__) { market = sampleMarket(); loaded = true; }
	$: title = data?.title || 'Bursa azi';
	$: maxTurnover = Math.max(1, ...(market?.shares || []).map(item => item.turnover));
	onMount(() => {
		const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
		const syncMotion = () => { reducedMotion = motion.matches; };
		syncMotion();
		motion.addEventListener('change', syncMotion);
		if (__ADMIN__) return () => motion.removeEventListener('change', syncMotion);
		let active = true;
		async function refresh() {
			if (document.hidden) return;
			const result = await fetchMarket();
			if (!active) return;
			market = result || (usableMarket(market) ? { ...market, stale: true } : null);
			loaded = true;
			if (market && data?.showNews !== false && !newsLoaded && !newsLoading) {
				newsLoading = true;
				try {
					const items = await fetchSectionNews(['bvb', 'bursa', 'bursa de valori bucuresti'], 3, Config.domain);
					if (active) { news = items; newsLoaded = true; }
				} finally { newsLoading = false; }
			}
		}
		refresh();
		const timer = setInterval(refresh, 5 * 60_000);
		document.addEventListener('visibilitychange', refresh);
		return () => { active = false; clearInterval(timer); document.removeEventListener('visibilitychange', refresh); motion.removeEventListener('change', syncMotion); };
	});
</script>

<Hydrator props={$$props} component="MarketStrip" hydrate={!__ADMIN__}>
	{#if !loaded || market}
		<section class="af-market" aria-label={title}>
			<div class="market-inner">
				{#if __ADMIN__}<p class="preview">Previzualizare · date demonstrative</p>{/if}
				<div class="strip">
					<div class="heading">
						<span class="eyebrow">PIEȚE ROMÂNEȘTI</span><strong><svg class="market-icon" viewBox="0 0 20 20" aria-hidden="true"><path d="M3 15V10M10 15V5M17 15V2" /></svg>{title}</strong>
					</div>
					<div class="quote-rail" class:before={canBack} class:after={canForward}>
					<div class="quotes" use:watchQuotes={market?.indices} on:scroll={measureQuotes}>
						{#if market}
							{#each market.indices as item, i (item.symbol)}
								<div class="quote" title={INDEX_NAMES[item.symbol]}>
									<span class="symbol">{item.symbol}</span>
									{#key `${item.value}:${item.change}`}<span class="reading" style={`--entry-delay:${i * 35}ms`}><span class="value">{number(item.value)}</span>
									<span class="change" class:up={item.change > 0} class:down={item.change < 0}><span aria-hidden="true">{direction(item.change)}</span> {change(item.change)}</span></span>{/key}
								</div>
							{/each}
						{:else}<div class="loading" role="status"><span class="sr-only">Se încarcă datele bursiere…</span>{#each [0, 1, 2, 3] as i}<span class="skeleton" aria-hidden="true" style={`--entry-delay:${i * 70}ms`}><span></span><span></span><span></span></span>{/each}</div>{/if}
					</div>
					</div>
					<div class="strip-tools">
						<div class="rail-nav" class:available={!!market && (canBack || canForward)}>
							<button class="rail-arrow" type="button" aria-label="Indicii anteriori" disabled={!canBack || !market} on:click={() => browseQuotes(-1)}><svg viewBox="0 0 20 20" aria-hidden="true"><path d="m12 5-5 5 5 5" /></svg></button>
							<button class="rail-arrow" type="button" aria-label="Indicii următori" disabled={!canForward || !market} on:click={() => browseQuotes(1)}><svg viewBox="0 0 20 20" aria-hidden="true"><path d="m8 5 5 5-5 5" /></svg></button>
						</div>
					</div>
				</div>
				<div class="source">
					{#if market}
						<span>Acțiuni BVB · {quoteLabel(market.quotedAt)}</span>
						{#if market.stale}<span>Actualizare indisponibilă · ultimele date disponibile</span>{/if}
					{:else}<span aria-hidden="true">Bursa de Valori București</span>{/if}
				</div>
				<div class="panel">
						<div class="panel-intro"><div><span class="eyebrow">ACTIVITATEA ȘEDINȚEI</span><h2>Cele mai tranzacționate</h2><p>Acțiuni din piața principală · clasament după valoarea tranzacțiilor</p></div></div>
						{#if !loaded}
							<div class="shares-grid" aria-hidden="true">{#each [0, 1, 2, 3, 4] as i}<div class="share-card skeleton share-skeleton" style={`--entry-delay:${i * 70}ms`}><span></span><span></span><span></span></div>{/each}</div>
						{:else if market.shares.length}
							<ol class="shares-grid" aria-label="Cele mai tranzacționate acțiuni">
								{#each market.shares as item, i (item.symbol)}
									<li style={`--row-delay:${i * 45}ms`}><a class="share-card" href={`https://www.bvb.ro/FinancialInstruments/Details/FinancialInstrumentsDetails.aspx?s=${encodeURIComponent(item.symbol)}`} target="_blank" rel="noopener noreferrer">
										<span class="card-top"><strong>{item.symbol}</strong><span class="card-arrow" aria-hidden="true">↗</span></span>
										<span class="company">{item.name}</span>
										<span class="share-price">{price(item.price)} <span>lei</span></span>
										<span class="change" class:up={item.change > 0} class:down={item.change < 0}><span aria-hidden="true">{direction(item.change)}</span> {change(item.change)}</span>
										<span class="turnover">Rulaj <strong>{turnover(item.turnover)} lei</strong></span>
										<span class="bar" aria-hidden="true"><span style={`width:${100 * item.turnover / maxTurnover}%`}></span></span>
										<span class="sr-only">Deschide pe BVB într-o filă nouă</span>
									</a></li>
								{/each}
							</ol>
						{:else}<p>Nu sunt disponibile tranzacții pentru această ședință.</p>{/if}
						{#if data?.showNews !== false && news.length}<div class="news"><h3>Contextul din spatele cifrelor</h3><ul>{#each news as item (item.id)}<li><a href={item.url}>{item.title} <span aria-hidden="true">→</span></a></li>{/each}</ul></div>{/if}
					</div>
			</div>
		</section>
	{/if}
</Hydrator>

<style>
	.af-market { background: var(--af-paper, #f7f3ec); color: var(--af-ink, #14161a); border-bottom: 1px solid var(--af-hairline, #ded5c6); font: 400 13px/1.5 var(--af-font-sans, Arial, sans-serif); width: 100%; min-width: 0; }
	.market-inner { max-width: var(--af-site-max, 62.5rem); margin-inline: auto; padding-inline: var(--af-gutter, 1rem); box-sizing: border-box; }
	button, a { -webkit-tap-highlight-color: transparent; }
	button { color: inherit; font: inherit; cursor: pointer; background: transparent; border: 0; transition: background .18s ease, border-color .18s ease, transform .18s ease; }
	button:disabled { cursor: default; }
	button:focus-visible, a:focus-visible { outline: 2px solid var(--af-focus, #1d6fe0); outline-offset: -2px; }
	button:hover:not(:disabled) { background: var(--af-surface-sunk, #efe9de); }
	.strip { display: flex; align-items: center; min-height: 112px; gap: 16px; }
	.heading { flex-shrink: 0; min-width: 0; max-width: 180px; display: flex; flex-direction: column; justify-content: center; gap: 5px; text-align: left; padding: 8px 0; border-radius: 6px; }
	.heading strong { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 22px; font-family: var(--af-font-serif, Georgia, serif); }
	.market-icon { display: inline-block; width: 18px; height: 18px; margin-right: 7px; color: var(--af-accent, #9a4e18); vertical-align: -1px; }
	svg { fill: none; stroke: currentColor; stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round; }
	.eyebrow { color: var(--af-accent, #9a4e18); font-size: 12px; font-weight: 600; letter-spacing: .06em; }
	.quote-rail { flex: 1; min-width: 0; position: relative; }
	.quote-rail::before, .quote-rail::after { content: ''; position: absolute; top: 0; bottom: 0; width: 22px; z-index: 1; pointer-events: none; opacity: 0; transition: opacity .2s; }
	.quote-rail::before { left: 0; background: linear-gradient(90deg, var(--af-paper, #f7f3ec), transparent); }
	.quote-rail::after { right: 0; background: linear-gradient(-90deg, var(--af-paper, #f7f3ec), transparent); }
	.quote-rail.before::before, .quote-rail.after::after { opacity: 1; }
	.quotes { display: flex; gap: 8px; min-width: 0; padding: 6px 0; overflow-x: auto; overflow-y: hidden; scrollbar-width: none; scroll-snap-type: x proximity; }
	.quotes::-webkit-scrollbar { display: none; width: 0; height: 0; }
	.quote { display: flex; flex-direction: column; justify-content: center; gap: 3px; padding: 9px 12px; box-sizing: border-box; height: 94px; flex: 1 0 132px; text-align: left; border: 1px solid var(--af-hairline, #ded5c6); border-radius: 8px; background: var(--af-surface, #fff); scroll-snap-align: start; }
	.symbol { font-weight: 700; font-size: 12px; letter-spacing: .035em; }
	.reading { display: flex; flex-direction: column; align-items: flex-start; gap: 3px; animation: quote-reveal .36s ease both; animation-delay: var(--entry-delay, 0ms); }
	.value { font: 400 16px/1.3 var(--af-font-mono, monospace); white-space: nowrap; }
	.change { font-variant-numeric: tabular-nums; white-space: nowrap; color: var(--af-flat, #5c6169); }
	.quote .change { font-size: 12px; line-height: 1.5; padding: 1px 6px; margin-left: -3px; border-radius: 4px; background: color-mix(in srgb, var(--af-flat, #5c6169) 8%, transparent); }
	.quote .change.up { background: color-mix(in srgb, var(--af-up, #0d724a) 9%, transparent); }
	.quote .change.down { background: color-mix(in srgb, var(--af-down, #b3261e) 8%, transparent); }
	.up { color: var(--af-up, #0d724a); }
	.down { color: var(--af-down, #b3261e); }
	.strip-tools { display: flex; flex-direction: column-reverse; align-items: center; flex-shrink: 0; }
	.rail-nav { display: none; }
	.rail-nav.available { display: flex; }
	.rail-arrow { width: 44px; height: 44px; display: inline-flex; align-items: center; justify-content: center; border-radius: 50%; color: var(--af-brand, #0e2a47); }
	.rail-arrow svg { width: 18px; height: 18px; }
	.rail-arrow:disabled { opacity: .25; }
	.loading { display: flex; gap: 8px; flex: 1; min-width: 0; }
	.skeleton { display: flex; flex-direction: column; justify-content: center; gap: 8px; height: 94px; flex: 1 0 132px; box-sizing: border-box; padding: 12px; border: 1px solid var(--af-hairline, #ded5c6); border-radius: 8px; background: var(--af-surface, #fff); }
	.skeleton span { display: block; height: 12px; width: 45%; border-radius: 3px; background: linear-gradient(100deg, var(--af-surface-sunk, #efe9de) 25%, var(--af-paper, #f7f3ec) 50%, var(--af-surface-sunk, #efe9de) 75%); background-size: 220% 100%; animation: shimmer 1.5s ease-in-out infinite; animation-delay: var(--entry-delay, 0ms); }
	.skeleton span:nth-child(2) { height: 18px; width: 85%; }
	.skeleton span:nth-child(3) { width: 55%; }
	.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }
	.source { display: grid; grid-template-columns: 1fr auto; gap: 0 12px; color: var(--af-ink-muted, #5c6169); padding: 5px 0 8px; font-size: 13px; }
	.panel { border-top: 1px solid var(--af-hairline, #ded5c6); padding: 28px 0; }
	.panel-intro { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 20px; }
	h2 { font: 600 26px/1.2 var(--af-font-serif, Georgia, serif); margin: 4px 0 8px; }
	p { margin: 0; color: var(--af-ink-muted, #5c6169); }
	a { color: var(--af-brand, #0e2a47); }
	.shares-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 10px; list-style: none; padding: 0; margin: 0; }
	.shares-grid li { min-width: 0; animation: row-reveal .36s ease both; animation-delay: var(--row-delay, 0ms); }
	.share-card { display: flex; flex-direction: column; min-width: 0; height: 100%; min-height: 204px; box-sizing: border-box; padding: 14px; border: 1px solid var(--af-hairline, #ded5c6); border-radius: 10px; background: var(--af-surface, #fff); color: var(--af-ink, #14161a); text-decoration: none; transition: transform .2s ease, border-color .2s ease, box-shadow .2s ease; }
	.card-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; font-size: 15px; }
	.card-arrow { color: var(--af-ink-muted, #5c6169); transition: transform .2s ease; }
	.company { display: block; min-height: 36px; margin-top: 2px; font-size: 12px; line-height: 1.5; color: var(--af-ink-muted, #5c6169); overflow-wrap: anywhere; }
	.share-price { margin-top: 12px; font: 500 20px/1.3 var(--af-font-mono, monospace); }
	.share-price > span { font: 400 12px var(--af-font-sans, Arial, sans-serif); color: var(--af-ink-muted, #5c6169); }
	.share-card .change { align-self: flex-start; margin: 5px 0 16px; font-size: 12px; }
	.turnover { display: flex; flex-wrap: wrap; gap: 0 4px; margin-top: auto; font-size: 11px; color: var(--af-ink-muted, #5c6169); }
	.turnover strong { font-weight: 500; font-variant-numeric: tabular-nums; color: var(--af-ink, #14161a); }
	.share-skeleton { justify-content: flex-start; gap: 15px; }
	.bar { display: block; height: 4px; border-radius: 4px; overflow: hidden; background: var(--af-surface-sunk, #efe9de); margin-top: 7px; }
	.bar span { display: block; height: 100%; border-radius: inherit; background: var(--af-brand, #0e2a47); opacity: .5; transform-origin: left; animation: bar-reveal .7s cubic-bezier(.22, 1, .36, 1) both; animation-delay: var(--row-delay, 0ms); transition: width .5s ease; }
	.news { border-top: 1px solid var(--af-hairline, #ded5c6); margin-top: 24px; padding-top: 16px; }
	h3 { margin: 0 0 12px; font-size: 15px; }
	.news ul { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
	.news a { display: block; min-height: 44px; font: 600 17px/1.4 var(--af-font-serif, Georgia, serif); text-decoration: none; }
	.news a span { display: inline-block; transition: transform .2s; }
	.news a:hover { text-decoration: underline; }
	.preview { color: var(--af-accent, #9a4e18); }
	@keyframes quote-reveal { from { opacity: .35; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
	@keyframes row-reveal { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
	@keyframes bar-reveal { from { transform: scaleX(0); } to { transform: scaleX(1); } }
	@keyframes shimmer { from { background-position: 100% 0; } to { background-position: -100% 0; } }
	@media (hover: hover) and (pointer: fine) {
		.share-card:hover { transform: translateY(-3px); border-color: var(--af-brand, #0e2a47); box-shadow: 0 6px 16px #0000000a; }
		.share-card:hover .card-arrow { transform: translate(2px, -2px); }
		.news a:hover span { transform: translateX(4px); }
	}
	@media (min-width: 600px) and (max-width: 900px) {
		.shares-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
	}
	@media (max-width: 767px) {
		.strip { gap: 0; flex-wrap: wrap; }
		.heading { flex: 1; max-width: calc(100% - 88px); min-height: 44px; padding-block: 6px; }
		.heading strong { font-size: 18px; }
		.market-icon { display: none; }
		.heading .eyebrow { display: none; }
		.strip-tools { order: 1; flex-direction: row; }
		.rail-nav { display: flex; visibility: hidden; }
		.rail-nav.available { visibility: visible; }
		.quote-rail { order: 2; flex-basis: 100%; }
		.quote, .skeleton { flex-basis: 145px; }
		.panel-intro { display: block; margin-bottom: 12px; }
		.source { grid-template-columns: 1fr; }
		h2 { font-size: 24px; }
		.share-card { padding: 12px; }
		.news ul { grid-template-columns: 1fr; gap: 12px; }
	}
	@media (max-width: 599px) {
		.shares-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
	}
	@media (prefers-reduced-motion: reduce) {
		.af-market *, .af-market *::before, .af-market *::after { animation: none !important; transition: none !important; scroll-behavior: auto !important; }
		.share-card:hover, .share-card:hover .card-arrow { transform: none; }
	}
</style>
